import { useEffect, useState, Fragment } from "react";
import api, { apiError, fmtUSD, fmtNum } from "../../../lib/api";
import { toast } from "sonner";

export function Holdings() {
  const [holdings, setHoldings] = useState([]);
  const [members, setMembers] = useState([]);
  const [marks, setMarks] = useState([]);
  const [markEdits, setMarkEdits] = useState({});
  const [notify, setNotify] = useState(true);
  const [f, setF] = useState({ user_id: "", company: "", share_class: "Common", units: "", entry_price: "", current_mark: "", entry_year: "2026" });
  const [busy, setBusy] = useState(false);
  const [distFor, setDistFor] = useState(null);
  const [df, setDf] = useState({ tranche: "", date: "", amount: "" });
  const [csvFile, setCsvFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  const load = () => {
    api.get("/admin/holdings").then((r) => setHoldings(r.data)).catch(() => {});
    api.get("/admin/marks").then((r) => setMarks(r.data)).catch(() => {});
  };
  useEffect(() => { load(); api.get("/admin/members").then((r) => setMembers(r.data.filter((m) => m.role === "member"))).catch(() => {}); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const mkey = (m) => `${m.company}|${m.share_class}`;

  const applyMarks = async () => {
    const updates = marks
      .filter((m) => markEdits[mkey(m)] !== undefined && markEdits[mkey(m)] !== "" && Number(markEdits[mkey(m)]) !== m.current_mark)
      .map((m) => ({ company: m.company, share_class: m.share_class, new_mark: Number(markEdits[mkey(m)]) }));
    if (updates.length === 0) {
      toast.error("No mark changes entered.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/marks", { updates, notify });
      toast.success(`${data.updated} positions re-marked. ${data.notified} members notified.`);
      setMarkEdits({});
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const create = async () => {
    setBusy(true);
    try {
      await api.post("/admin/holdings", {
        ...f, units: Number(f.units), entry_price: Number(f.entry_price), current_mark: Number(f.current_mark),
      });
      toast.success("Position assigned.");
      setF({ ...f, company: "", units: "", entry_price: "", current_mark: "" });
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const addDistribution = async (h) => {
    setBusy(true);
    try {
      await api.post(`/admin/holdings/${h.holding_id}/distributions`, {
        tranche: df.tranche || "1 of 1", date: df.date, amount: Number(df.amount),
      });
      toast.success("Distribution recorded. The member has been notified.");
      setDistFor(null);
      setDf({ tranche: "", date: "", amount: "" });
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const importCsv = async () => {
    if (!csvFile) return;
    setImporting(true);
    setImportResult(null);
    try {
      const fd = new FormData();
      fd.append("file", csvFile);
      const { data } = await api.post("/admin/distributions/import", fd);
      setImportResult(data);
      toast.success(`Imported ${data.posted} distribution${data.posted === 1 ? "" : "s"}${data.skipped ? `, ${data.skipped} skipped` : ""}.`);
      setCsvFile(null);
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setImporting(false); }
  };

  const downloadTemplate = () => {
    const csv = "member_email,company,tranche,date,amount\nmember@solera.vc,Ferrous Kettle Labs,1 of 1,12 September 2026,5000\n";
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "solera-distributions-template.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <div className="max-w-xl space-y-3" data-testid="admin-holding-form">
        <span className="t-label">Assign a position to a member</span>
        <select className="input-solera" value={f.user_id} onChange={(e) => setF({ ...f, user_id: e.target.value })} data-testid="holding-member-select">
          <option value="">Select member</option>
          {members.map((m) => <option key={m.user_id} value={m.user_id}>{`${m.name}, ${m.email}`}</option>)}
        </select>
        <div className="grid grid-cols-2 gap-3">
          <input className="input-solera" placeholder="Company" value={f.company} onChange={(e) => setF({ ...f, company: e.target.value })} data-testid="holding-company-input" />
          <input className="input-solera" placeholder="Share class" value={f.share_class} onChange={(e) => setF({ ...f, share_class: e.target.value })} />
          <input className="input-solera fig" type="number" placeholder="Units" value={f.units} onChange={(e) => setF({ ...f, units: e.target.value })} data-testid="holding-units-input" />
          <input className="input-solera fig" placeholder="Entry year" value={f.entry_year} onChange={(e) => setF({ ...f, entry_year: e.target.value })} />
          <input className="input-solera fig" type="number" step="0.01" placeholder="Entry price" value={f.entry_price} onChange={(e) => setF({ ...f, entry_price: e.target.value })} data-testid="holding-entry-input" />
          <input className="input-solera fig" type="number" step="0.01" placeholder="Current mark" value={f.current_mark} onChange={(e) => setF({ ...f, current_mark: e.target.value })} data-testid="holding-mark-input" />
        </div>
        <button className="btn-primary" disabled={busy || !f.user_id || !f.company || !f.units} onClick={create} data-testid="holding-submit-button">Assign position</button>
      </div>
      <div className="hairline mt-10 pt-6 space-y-3 max-w-2xl" data-testid="distribution-import">
        <span className="t-label">Import distributions from CSV</span>
        <p className="t-cap text-muted-s">
          Columns, in order: member_email, company, tranche, date, amount. The company must match a position the member holds. Each row posts a distribution, issues an advice PDF and notifies the member.
        </p>
        <div className="flex items-center gap-4 flex-wrap">
          <input type="file" accept=".csv" onChange={(e) => setCsvFile(e.target.files[0])} data-testid="distribution-csv-input" className="t-small text-muted-s" />
          <button className="btn-ghost" disabled={!csvFile || importing} onClick={importCsv} data-testid="distribution-import-button">
            {importing ? "Importing" : "Import distributions"}
          </button>
          <button className="t-label hover:!text-[#E8E6E1]" onClick={downloadTemplate} data-testid="distribution-template-button">Download template</button>
        </div>
        {importResult && (
          <div className="t-small text-muted-s" data-testid="distribution-import-result">
            Posted {importResult.posted}. Skipped {importResult.skipped}.
            {importResult.errors?.length > 0 && (
              <ul className="mt-2 space-y-1">
                {importResult.errors.map((er, i) => <li key={`${i}-${er.slice(0, 24)}`} className="t-cap text-muted-s">{er}</li>)}
              </ul>
            )}
          </div>
        )}
      </div>

      <table className="table-solera mt-12" data-testid="admin-holdings-table">
        <thead><tr><th>Member</th><th>Position</th><th>Class</th><th className="num">Units</th><th className="num">Entry</th><th className="num">Mark</th><th className="num">Distributions</th><th></th></tr></thead>
        <tbody>
          {holdings.map((h) => {
            const dtot = (h.distributions || []).reduce((a, d) => a + d.amount, 0);
            return (
              <Fragment key={h.holding_id}>
                <tr>
                  <td className="text-muted-s">{h.member_email}</td>
                  <td className="text-bone">{h.company}</td>
                  <td className="text-muted-s">{h.share_class}</td>
                  <td className="num text-bone">{fmtNum(h.units)}</td>
                  <td className="num text-bone">{h.entry_price.toFixed(2)}</td>
                  <td className="num text-bone">{h.current_mark.toFixed(2)}</td>
                  <td className="num text-muted-s t-small">{dtot ? `${fmtUSD(dtot)} (${h.distributions.length})` : "none"}</td>
                  <td className="text-right whitespace-nowrap">
                    <button onClick={() => { setDistFor(distFor === h.holding_id ? null : h.holding_id); setDf({ tranche: "", date: "", amount: "" }); }}
                      className="t-label hover:!text-[#E8E6E1]" data-testid={`distribution-toggle-${h.holding_id}`}>
                      {distFor === h.holding_id ? "Cancel" : "Add distribution"}
                    </button>
                  </td>
                </tr>
                {distFor === h.holding_id && (
                  <tr>
                    <td colSpan={8}>
                      <div className="py-4 max-w-xl space-y-3" data-testid={`distribution-form-${h.holding_id}`}>
                        <span className="t-label">Record a distribution, {h.company}</span>
                        <div className="grid grid-cols-3 gap-3">
                          <input className="input-solera" placeholder="Tranche, e.g. 1 of 3" value={df.tranche}
                            onChange={(e) => setDf({ ...df, tranche: e.target.value })} data-testid="distribution-tranche-input" />
                          <input className="input-solera" placeholder="Date, e.g. 14 March 2026" value={df.date}
                            onChange={(e) => setDf({ ...df, date: e.target.value })} data-testid="distribution-date-input" />
                          <input className="input-solera fig" type="number" placeholder="Amount USD" value={df.amount}
                            onChange={(e) => setDf({ ...df, amount: e.target.value })} data-testid="distribution-amount-input" />
                        </div>
                        <button className="btn-primary" disabled={busy || !df.date || !df.amount} onClick={() => addDistribution(h)} data-testid="distribution-submit-button">
                          {busy ? "Recording" : "Record distribution and notify"}
                        </button>
                      </div>
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>

      <div className="mt-16 max-w-2xl" data-testid="quarterly-marks">
        <span className="t-label">Quarterly marks</span>
        <p className="t-cap text-muted-s mt-2">
          Set a new mark per position class. It applies to every holding of that class across all
          members at once.
        </p>
        <table className="table-solera mt-4">
          <thead><tr><th>Position</th><th>Class</th><th className="num">Holdings</th><th className="num">Current mark</th><th className="num">New mark</th></tr></thead>
          <tbody>
            {marks.map((m) => (
              <tr key={mkey(m)}>
                <td className="text-bone">{m.company}</td>
                <td className="text-muted-s">{m.share_class}</td>
                <td className="num text-bone">{m.positions}</td>
                <td className="num text-bone">{m.current_mark.toFixed(2)}</td>
                <td className="num">
                  <input className="input-solera fig !py-1 !px-2 w-24 text-right" type="number" step="0.01"
                    placeholder={m.current_mark.toFixed(2)} value={markEdits[mkey(m)] ?? ""}
                    onChange={(e) => setMarkEdits({ ...markEdits, [mkey(m)]: e.target.value })}
                    data-testid={`mark-input-${m.company.replace(/[^a-z0-9]/gi, "-").toLowerCase()}-${m.share_class.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="flex items-center gap-4 mt-4 flex-wrap">
          <button className={`option-card !p-3 w-auto ${notify ? "selected" : ""}`} onClick={() => setNotify(!notify)} data-testid="marks-notify-toggle">
            <span className="t-small">Notify affected members</span>
          </button>
          <button className="btn-primary" disabled={busy} onClick={applyMarks} data-testid="marks-apply-button">
            {busy ? "Applying" : "Apply marks"}
          </button>
        </div>
      </div>
    </div>
  );
}
